import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import "./composer-snake.css";

/*
 * Widget glass composer yılanı (Redraw "gradient-box" kuyruğu).
 *
 * SVG stroke genişliği dash boyunca değişemez; kaynağın NeonTaper eğrisi
 * `mix(tail, head, pow(t, 3))` basamaklanır: her katman daha kısa dash,
 * aynı kafa noktası, bir kademe daha kalın. En kısa + yuvarlak cap damla
 * gibi okunur.
 *
 * RESPONSIVE: dash geometrisi `pathLength=100` ile çevreye normalize.
 * Kalınlıklar `scale = clamp(çevre / 1500, 0.4, 1)` ile ölçeklenir
 * (1500 ≈ masaüstü pill; telefonda ~0.5).
 *
 * GLOW iOS WebKit'te CSS `filter` ile ÇİZİLMEZ: SVG çocuklarında yok
 * sayılıyor ve `overflow: visible` olsa bile viewport'a kırpılıyor.
 * Masaüstü Chrome'un cihaz emülasyonu Blink olduğu için bunu gizler.
 * Hale bu yüzden <feGaussianBlur>, SVG de blur'un sığacağı kadar pad'li.
 */

const SNAKE_TRAIL = 38;
const SNAKE_TAIL_W = 1.7;
const SNAKE_HEAD_W = 9.5;
/*
 * Kalınlık pill ile birlikte BÜYÜMEZ, sadece biraz açılır: telefon pill'i
 * (~700px çevre) ile masaüstü pill'i (~1500px) arasında 0.40 → 0.60. Ölçek
 * çevreyle doğrusal 1'e çıkınca geniş inputlarda kafa ve hale inputun
 * kendisi kadar yer kaplıyordu.
 */
const SNAKE_PHONE_PERIMETER = 700;
const SNAKE_DESK_PERIMETER = 1500;
const SNAKE_MIN_SCALE = 0.4;
const SNAKE_MAX_SCALE = 0.6;
/** Bloom + kafa halesinin SVG viewport'una sığması için (Safari clip). */
const SNAKE_GLOW_PAD = 40;
/**
 * Kaynağın SidedGlow'u içeride 12, dışarıda 65 sigma kullanır — hale
 * gövdenin DIŞINA açılır. Simetrik blur, halenin yarısını pill'in içine
 * boşaltıyor ve inputun altında blurlu bir yüzey varmış gibi duruyordu.
 * Maske içeriyi kısar; kaynağın sigma oranı olan 0.18, bloom'un köşelerde
 * kendi üstüne bindiği yerlerde hâlâ görünür bir pus bırakıyordu.
 */
const SNAKE_INNER_GLOW = 0.08;
/** Kaynağın `period: cycle * 3` — palet üç turda bir kez kayar. */
const SNAKE_PALETTE_MS = 18000;

const snakeScaleFor = (width: number, height: number, radius: number) => {
  const perimeter = 2 * (width + height) - 8 * radius + 2 * Math.PI * radius;
  const t =
    (perimeter - SNAKE_PHONE_PERIMETER) /
    (SNAKE_DESK_PERIMETER - SNAKE_PHONE_PERIMETER);
  const scale = SNAKE_MIN_SCALE + (SNAKE_MAX_SCALE - SNAKE_MIN_SCALE) * t;
  return Math.min(SNAKE_MAX_SCALE, Math.max(SNAKE_MIN_SCALE, scale));
};

const snakeStyle = (
  len: number,
  width: number,
  scale: number
): CSSProperties =>
  ({
    strokeWidth: width * scale,
    strokeDasharray: `${String(len)} ${String(100 - len)}`,
    "--composer-snake-phase": String(SNAKE_TRAIL - len),
  }) as CSSProperties;

const SNAKE_TAPER = [38, 26, 18, 12, 8, 5, 3.2, 2, 1.2, 0.6].map((len) => ({
  len,
  width:
    SNAKE_TAIL_W +
    (SNAKE_HEAD_W - SNAKE_TAIL_W) * Math.pow(1 - len / SNAKE_TRAIL, 3),
}));

/*
 * Beyaz çekirdek kaynakta `setStroke(NeonTaper, { scale: 0.3 })`: AYNI
 * daralma eğrisi, gövdenin 0.3 katı, kuyruğun ta ucundan kafaya kadar.
 * Kendi başına 10 birimlik bir eğri olarak kurulmuştu; o yüzden kafanın
 * biraz gerisinde biten kısa bir beyaz çizgi gibi duruyordu.
 * Her ikinci kademe yeter: çizgi ince, filtreli katman sayısı yarıya iner.
 */
const SNAKE_CORE_SCALE = 0.3;
const SNAKE_CORE = SNAKE_TAPER.filter((_, i) => i % 2 === 0).map((step) => ({
  len: step.len,
  width: step.width * SNAKE_CORE_SCALE,
}));

/*
 * Palindrom palet: dört çapa gidiş, üçü dönüş (a→b→c→d→c→b→a).
 * Çapalar aktif modülün hue ailesinden (composer-snake.css); dikişsiz
 * tekrar eder, ara duraklar oklab karışımı.
 */
const SNAKE_PALETTE = [
  "a",
  "ab",
  "b",
  "bc",
  "c",
  "cd",
  "d",
  "cd",
  "c",
  "bc",
  "b",
  "ab",
  "a",
];

interface ComposerSnakeProps {
  /** Input pill'inin köşe yarıçapı (px). İç kabuk `rounded-3xl` = 24. */
  radius?: number;
}

export const ComposerSnake = ({ radius = 24 }: ComposerSnakeProps) => {
  const rawId = useId().replace(/:/g, "");
  const gradId = `composer-snake-grad-${rawId}`;
  const bloomFilterId = `composer-snake-bloom-${rawId}`;
  const stepFilterId = `composer-snake-step-${rawId}`;
  const coreFilterId = `composer-snake-core-${rawId}`;
  const outwardMaskId = `composer-snake-outward-${rawId}`;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0, scale: 1 });

  useLayoutEffect(() => {
    const host = svgRef.current?.parentElement;
    if (!host) return;

    const update = () => {
      const rect = host.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setBox({
          width: rect.width,
          height: rect.height,
          scale: snakeScaleFor(rect.width, rect.height, radius),
        });
      }
    };
    update();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(host);
    return () => observer.disconnect();
  }, [radius]);

  const { width, height, scale } = box;
  const pad = SNAKE_GLOW_PAD;
  const outerW = width + 2 * pad;
  const outerH = height + 2 * pad;
  const bloomStd = 13 * scale;
  const stepStd = 0.9 * scale;
  const coreStd = 0.7 * scale;
  /*
   * Kaynakta çekirdeğin halesi `Feather.glow(4)`, kafa kalınlığı 9 — yani
   * çizginin yarısı kadar. Geniş çekirdeğe göre ayarlanmış 3.5 değeri, 0.3
   * kata inen çizgide beyazın rengi yutmasına yol açıyordu. Renkli büyük
   * hale bloom katmanının işi.
   */
  const glowStd = 1.3 * scale;
  /*
   * Palet bir kez tüm genişliği kaplıyor (kaynakta da palet kutu çizgisine
   * tek turda yayılır). Yarım genişlikte renkler çok hızlı değişiyordu.
   */
  const tile = Math.max(1, outerW);

  /*
   * Filtre bölgeleri userSpaceOnUse ve tam pad'li kutu. Bbox yüzdesi
   * (-80%/260%) 8-bit premultiply yuvarlamasıyla bölgenin tamamına düz
   * kenarlı soluk bir pus basıyordu — ekranda dikdörtgen bir yüzey.
   */
  const outerBox = { x: -pad, y: -pad, width: outerW, height: outerH };
  const region = { filterUnits: "userSpaceOnUse" as const, ...outerBox };

  return (
    <svg
      ref={svgRef}
      className="composer-snake"
      aria-hidden="true"
      viewBox={`${String(-pad)} ${String(-pad)} ${String(outerW)} ${String(outerH)}`}
      style={
        {
          left: -pad,
          top: -pad,
          width: outerW,
          height: outerH,
          "--composer-snake-scale": String(scale),
        } as CSSProperties
      }
    >
      <defs>
        {/*
         * Palet kutu çizgisine sabit (kaynağın `anchor: "source"`) ve
         * döngüsel: son durak ilkiyle aynı renk, spreadMethod=repeat.
         * Bir tile'lık kayma = kaynağın `gradient.time` palet kayması.
         * Bu, eskiden <g> üzerinde duran hue-rotate filtresinin yerini
         * alır — o filtre pusun ta kendisiydi.
         */}
        <linearGradient
          id={gradId}
          gradientUnits="userSpaceOnUse"
          x1={-pad}
          y1={0}
          x2={-pad + tile}
          y2={0}
          spreadMethod="repeat"
        >
          {SNAKE_PALETTE.map((tone, i) => (
            <stop
              key={`${tone}-${String(i)}`}
              className={`composer-snake-stop-${tone}`}
              offset={`${String((i / (SNAKE_PALETTE.length - 1)) * 100)}%`}
            />
          ))}
          <animateTransform
            attributeName="gradientTransform"
            type="translate"
            from="0 0"
            to={`${String(tile)} 0`}
            dur={`${String(SNAKE_PALETTE_MS / 1000)}s`}
            repeatCount="indefinite"
          />
        </linearGradient>

        {/* SidedGlow: hale dışarı açılır, içeride kısılır. */}
        <mask id={outwardMaskId} maskUnits="userSpaceOnUse" {...outerBox}>
          <rect
            x={-pad}
            y={-pad}
            width={outerW}
            height={outerH}
            fill="#fff"
          />
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            rx={radius}
            fill="#fff"
            fillOpacity={SNAKE_INNER_GLOW}
          />
        </mask>

        <filter {...region} id={bloomFilterId} colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation={bloomStd} />
        </filter>
        <filter {...region} id={stepFilterId} colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation={stepStd} />
        </filter>
        <filter {...region} id={coreFilterId} colorInterpolationFilters="sRGB">
          <feGaussianBlur
            in="SourceGraphic"
            stdDeviation={coreStd}
            result="soft"
          />
          <feDropShadow
            className="composer-snake-core-glow"
            dx="0"
            dy="0"
            stdDeviation={glowStd}
            floodOpacity="0.75"
          />
        </filter>
      </defs>

      <g mask={`url(#${outwardMaskId})`}>
        <rect
          className="composer-snake-trail composer-snake-bloom"
          x={0}
          y={0}
          width={width}
          height={height}
          rx={radius}
          pathLength={100}
          stroke={`url(#${gradId})`}
          filter={`url(#${bloomFilterId})`}
        />
        {/*
         * Kafa halesi — kaynağın SidedGlow `bloom: 90` gaussiyanı.
         * Noktaya yakın dash + kalın yuvarlak cap + blur = ışık topu.
         */}
        <rect
          className="composer-snake-trail composer-snake-headbloom"
          x={0}
          y={0}
          width={width}
          height={height}
          rx={radius}
          pathLength={100}
          stroke={`url(#${gradId})`}
          filter={`url(#${bloomFilterId})`}
          style={snakeStyle(3.4, 21, scale)}
        />
      </g>

      {/* Gövde ve çekirdek rim'de duruyor; maskesiz. */}
      {SNAKE_TAPER.map((step) => (
        <rect
          key={step.len}
          className="composer-snake-trail composer-snake-step"
          x={0}
          y={0}
          width={width}
          height={height}
          rx={radius}
          pathLength={100}
          stroke={`url(#${gradId})`}
          filter={`url(#${stepFilterId})`}
          style={snakeStyle(step.len, step.width, scale)}
        />
      ))}
      {SNAKE_CORE.map((step) => (
        <rect
          key={step.len}
          className="composer-snake-trail composer-snake-core"
          x={0}
          y={0}
          width={width}
          height={height}
          rx={radius}
          pathLength={100}
          /*
           * Açık temada omurga gradient'in kendisi: beyaz bir çekirdek beyaz
           * pill üstünde görünmüyor, geriye sadece bulanık renk bandı
           * kalıyordu. Koyu temada CSS `stroke` bunu ezip beyaz-sıcak
           * çekirdeği geri getiriyor (CSS her zaman presentation
           * attribute'undan güçlü).
           */
          stroke={`url(#${gradId})`}
          filter={`url(#${coreFilterId})`}
          style={snakeStyle(step.len, step.width, scale)}
        />
      ))}
    </svg>
  );
};
